import React, { useState, useMemo } from 'react';
import { Card, List, Button, InputNumber, Space, Alert, Typography, Divider } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Renders one league's ranked add/drop lists and lets the user preview/submit
// the auto-generated tiered MFL waiver claim chain for that league.
const LeagueCard = ({
  leagueInfo,
  selectedAdds,
  selectedDrops,
  onReorderAdd,
  onReorderDrop,
  onBidChange,
  onRemoveAdd,
  onRemoveDrop,
  onPreview,
  onSubmit,
  onRefreshLive,
  liveLoading,
  openSlots,
  bbidBalance
}) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Maps player IDs back to display names for the generated claim chain
  const playerNameById = useMemo(() => {
    const map = {};
    [...selectedAdds, ...selectedDrops].forEach((p) => { map[p.playerID] = p.playerName; });
    return map;
  }, [selectedAdds, selectedDrops]);
  const nameFor = (playerID) => playerNameById[playerID] || playerID;

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setSubmitResult(null);
    try {
      const result = await onPreview(leagueInfo.leagueID);
      setPreview(result);
    } catch (err) {
      setError(err.message || 'Failed to preview claims');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await onSubmit(leagueInfo.leagueID);
      setSubmitResult(result);
    } catch (err) {
      setError(err.message || 'Failed to submit claims');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title={`League: ${leagueInfo.leagueName}`} style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
        <Text strong>
          Open roster slots: {openSlots !== null ? openSlots : '?'} | FAAB available: ${bbidBalance || 0}
        </Text>
      </div>
      <Text strong>To Add (ranked, highest priority first):</Text>
      <List
        size="small"
        dataSource={selectedAdds}
        locale={{ emptyText: 'No players selected to add' }}
        renderItem={(player, index) => (
          <List.Item
            actions={[
              <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onReorderAdd(leagueInfo.leagueID, index, index - 1)} />,
              <Button size="small" icon={<ArrowDownOutlined />} disabled={index === selectedAdds.length - 1} onClick={() => onReorderAdd(leagueInfo.leagueID, index, index + 1)} />,
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => onRemoveAdd(leagueInfo.leagueID, index)} />
            ]}
          >
            <Space>
              <Text>{index + 1}. {player.playerName}</Text>
              <InputNumber
                min={0}
                addonBefore="$"
                value={player.bidAmount || 0}
                onChange={(value) => onBidChange(leagueInfo.leagueID, index, value)}
              />
            </Space>
          </List.Item>
        )}
      />

      <Divider style={{ margin: '12px 0' }} />

      <Text strong>To Drop (ranked, first to be dropped first):</Text>
      <List
        size="small"
        dataSource={selectedDrops}
        locale={{ emptyText: 'No players selected to drop' }}
        renderItem={(player, index) => (
          <List.Item
            actions={[
              <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onReorderDrop(leagueInfo.leagueID, index, index - 1)} />,
              <Button size="small" icon={<ArrowDownOutlined />} disabled={index === selectedDrops.length - 1} onClick={() => onReorderDrop(leagueInfo.leagueID, index, index + 1)} />,
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => onRemoveDrop(leagueInfo.leagueID, index)} />
            ]}
          >
            {index + 1}. {player.playerName}
          </List.Item>
        )}
      />

      <Divider style={{ margin: '12px 0' }} />

      <Space>
        <Button onClick={handlePreview} loading={loading} disabled={selectedAdds.length === 0}>
          Preview Claims
        </Button>
        <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={!preview || preview.claims.length === 0}>
          Submit Claims to MFL
        </Button>
        <Button onClick={() => onRefreshLive(leagueInfo.leagueID)} loading={liveLoading}>
          Refresh Live League
        </Button>
      </Space>

      {error && <Alert style={{ marginTop: 12 }} type="error" message={error} />}

      {preview && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">
            Open roster slots: {preview.capacity.openSlots} | FAAB available: ${preview.capacity.bbidBalance}
          </Text>
          {preview.overBudget && (
            <Alert style={{ marginTop: 8 }} type="warning" message="Total bid amount exceeds your available FAAB balance." />
          )}
          {preview.skippedAdds.length > 0 && (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              message={`${preview.skippedAdds.length} add(s) skipped - no open roster slots and no drop candidates selected.`}
            />
          )}
          <List
            style={{ marginTop: 8 }}
            size="small"
            header={<Text strong>Generated claim chain (tried in order):</Text>}
            dataSource={preview.claims}
            renderItem={(claim) => (
              <List.Item>
                #{claim.priority}: Add {nameFor(claim.playerID)} for ${claim.bidAmount}
                {claim.dropPlayerID !== '0000' ? ` (drop ${nameFor(claim.dropPlayerID)})` : ' (no drop needed)'}
              </List.Item>
            )}
          />
        </div>
      )}

      {submitResult && (
        <Alert style={{ marginTop: 12 }} type="success" message={submitResult.mflResponse || 'Claims submitted'} />
      )}
    </Card>
  );
};

export default LeagueCard;
